import { z } from "zod";

/**
 * A person's preferences (PLT-03; profile template Req 1, 2, 4, 6, 7 and
 * the LMS's own accessibility set — templates-port-plan.md §3). Stored as
 * one JSON document on the `preference` row, read with /v1/me, and the
 * appearance subset cached in the browser so public/theme.js paints the
 * first frame right before the app loads.
 *
 * Two schemas, on purpose (template pitfall 1): zod 4's `.partial()` keeps
 * `.default()`, so a one-field update built from the full schema would
 * reset every other field. The update schema is the same fields with no
 * defaults; `preferences.test.ts` asserts a one-field update leaves the
 * rest alone.
 */
/** A registry id: lower-case words joined by hyphens (the template's rule). */
const NUDGE_ID = /^[a-z][a-z0-9-]{0,31}$/;

/**
 * One answer to one nudge. "later" is *until the next sign-in*: `signIn` is the id of the sign-in it
 * was said in (the session's `signedInAt`, set once at the callback and kept across step-ups — the
 * template's pitfall 6 — so a password change does not bring the banner back). `at` is when, for the trail.
 */
export const NudgeStateSchema = z.discriminatedUnion("state", [
  // A "later" with no sign-in to name would hide the nudge until nothing in particular: refused.
  z.object({ state: z.literal("later"), at: z.string().datetime(), signIn: z.number().int().positive() }).strict(),
  z.object({ state: z.literal("never"), at: z.string().datetime() }).strict(),
]);
export type NudgeState = z.infer<typeof NudgeStateSchema>;

const fields = {
  theme: z.enum(["system", "light", "dark"]),
  motion: z.enum(["auto", "standard", "reduced"]),
  textSize: z.enum(["default", "large", "x-large"]),
  contrast: z.enum(["auto", "default", "more"]),
  /** `default` or `relaxed` line height for reading (WCAG 1.4.12 is met either way; this is comfort). */
  lineSpacing: z.enum(["default", "relaxed"]),
  /** `auto` follows the device; otherwise an IANA zone. */
  timeZone: z.string().min(1).max(64),
  dateFormat: z.enum(["auto", "iso", "us", "eu"]),
  /** Where a sign-in lands (Req 6); each area checks the caller may see it and falls back to home. */
  startPage: z.enum(["home", "training", "approvals", "program"]),
  /** Media never starts on its own (CNT-27's default for this population); a person may turn autoplay on. */
  autoplay: z.boolean(),
  /** Captions on by default for every video; transcript shown first where one exists. */
  captions: z.boolean(),
  transcriptFirst: z.boolean(),
  /** Reminders by email or only in the product (the minimal Req 22; an ADA communication preference). */
  reminderChannel: z.enum(["email", "in-app"]),
  /**
   * What the person said to each nudge (PLT-06, in-app-nudges template): kept with the preferences so
   * "Dismiss forever" on a laptop holds on the phone, and audited like any other preference. The whole
   * map is written at once; ids the registry no longer has are ignored, not pruned.
   */
  nudges: z.record(z.string().regex(NUDGE_ID), NudgeStateSchema),
};

export const PreferencesSchema = z.object({
  theme: fields.theme.default("system"),
  motion: fields.motion.default("auto"),
  textSize: fields.textSize.default("default"),
  contrast: fields.contrast.default("auto"),
  lineSpacing: fields.lineSpacing.default("default"),
  timeZone: fields.timeZone.default("auto"),
  dateFormat: fields.dateFormat.default("auto"),
  startPage: fields.startPage.default("home"),
  autoplay: fields.autoplay.default(false),
  captions: fields.captions.default(true),
  transcriptFirst: fields.transcriptFirst.default(false),
  reminderChannel: fields.reminderChannel.default("email"),
  nudges: fields.nudges.default({}),
});
export type Preferences = z.infer<typeof PreferencesSchema>;

/**
 * The same fields, none required, none defaulted: what PATCH /v1/me/preferences accepts. `nudges` is
 * merged entry by entry on the server, so a patch carries only the answers it changes, and a null
 * entry takes that answer back.
 */
export const PreferencesUpdateSchema = z
  .object({ ...fields, nudges: z.record(z.string().regex(NUDGE_ID), NudgeStateSchema.nullable()) })
  .partial()
  .strict();
export type PreferencesUpdate = z.infer<typeof PreferencesUpdateSchema>;

export const DEFAULT_PREFERENCES: Preferences = PreferencesSchema.parse({});

/** Merge a stored document (any shape, possibly older) with the defaults: unknown keys dropped, bad values replaced. */
export function withDefaults(stored: unknown): Preferences {
  const parsed = PreferencesSchema.safeParse(stored ?? {});
  if (parsed.success) return parsed.data;
  // Field by field, so one bad value does not reset the rest.
  const out: Record<string, unknown> = { ...DEFAULT_PREFERENCES };
  if (stored && typeof stored === "object") {
    for (const [key, value] of Object.entries(stored)) {
      const field = (fields as Record<string, z.ZodType>)[key];
      if (field && field.safeParse(value).success) out[key] = value;
    }
    // Nudge answers entry by entry too: one unreadable answer must not bring back every banner the person dismissed.
    const nudges = (stored as Record<string, unknown>)["nudges"];
    if (nudges && typeof nudges === "object" && !fields.nudges.safeParse(nudges).success) {
      out["nudges"] = Object.fromEntries(Object.entries(nudges).filter(([id, answer]) => fields.nudges.safeParse({ [id]: answer }).success));
    }
  }
  return out as Preferences;
}

/** PATCH /v1/me/profile (Req 8): the display name, the one profile field a person edits themselves; legal names are the record's (AGR-01). */
export const ProfileUpdateSchema = z.object({ displayName: z.string().trim().min(1).max(80) }).strict();
export type ProfileUpdate = z.infer<typeof ProfileUpdateSchema>;
